import { UseMutationOptions, useMutation } from "@tanstack/react-query";
import axiosAuth from "../axiosAuth";
import { CREATE_TASKASSIGNMENT, TASK_FILE_UPLOAD } from "./apiRoutes";
import { removeEmptyFields } from "@/lib/utils";

/**
 * -----------------------------
 * Payload (as per Swagger)
 * -----------------------------
 */
export interface Assignment {
  school: string;
  role: string;
  assignTo: string;
}

export interface CreateTaskAssignmentPayload {
  taskName: string;
  priority: "LOW" | "MEDIUM" | "HIGH";
  startDate: string;
  endDate: string;
  taskStatus: string;
  taskAttachment: string | File; // can be file or UUID
  taskAttachmentName?: string; // 👈 added for file_name
  taskNotes: string;
  assignments: Assignment[];
}

/**
 * -----------------------------
 * Response (as per Swagger)
 * -----------------------------
 */
export interface TaskAssignmentData {
  taskId: string;
  taskName: string;
  priority: string;
  startDate: string;
  endDate: string;
  taskStatus: string;
  taskAttachment: string;
  taskAttachmentName?: string; // 👈 optional in response
  isActive: boolean;
  latestNote: string;
  school: string;
  role: string;
  assignTo: string;
  createdBy: string;
  lastUpdatedBy: string;
  _id: string;
  __v: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTaskAssignmentResponse {
  status: number;
  error: boolean;
  count: number;
  issues: any[];
  data: TaskAssignmentData[];
  message: string;
}

/**
 * -----------------------------
 * File Upload Helper
 * -----------------------------
 */
const uploadTaskFileAndGetInfo = async (file: File) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("file_type", "task_attachment");

  const res = await axiosAuth.post(TASK_FILE_UPLOAD, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  const data = res?.data?.data;
  return {
    attachment_id: data?.attachment_id as string,
    file_name: data?.file_name as string,
  };
};

/**
 * -----------------------------
 * Mutation Hook
 * -----------------------------
 */
export const useCreateTaskAssignmentMutation = (
  options?: UseMutationOptions<
    CreateTaskAssignmentResponse,
    Error,
    CreateTaskAssignmentPayload
  >
) => {
  return useMutation({
    mutationFn: async (payload: CreateTaskAssignmentPayload) => {
      let taskAttachmentUuid = payload.taskAttachment;
      let taskAttachmentName = payload.taskAttachmentName;

      // Check if a file was uploaded
      if (taskAttachmentUuid && typeof taskAttachmentUuid !== "string") {
        const fileInfo = await uploadTaskFileAndGetInfo(
          taskAttachmentUuid as File
        );
        taskAttachmentUuid = fileInfo.attachment_id;
        taskAttachmentName = fileInfo.file_name;
      }

      const normalizedPayload = {
        ...payload,
        taskAttachment: taskAttachmentUuid,
        taskAttachmentName, // 👈 added file_name
      };

      const response = await axiosAuth.post<CreateTaskAssignmentResponse>(
        CREATE_TASKASSIGNMENT,
        removeEmptyFields(normalizedPayload)
      );

      return response.data;
    },
    ...options,
  });
};
